import React, { useState, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import PhoneInput from "../../PhoneInput";
import { FormErrorContainer } from "../../FormErrorContainer";
import {
  ContentContainer,
  ModalTitle,
  ModalSubtitle,
  SectionDivider,
  SectionLabel,
  InfoBanner,
  InfoBannerText,
  FieldContainer,
  FieldLabel,
  RequiredMark,
  StyledInput,
  PhoneInputWrapper,
  ButtonsContainer,
  CancelButton,
  SubmitButton,
} from "./AddTenantModal.styled";
import {
  tenantsService,
  CreateTenantParams,
  Tenant,
} from "../../../services/tenantsService";
import {
  validateUSPhoneNumber,
  cleanPhoneNumber,
} from "../../../utils/phoneValidation";
import { validateName } from "../../../utils/nameValidation";
import { validateEmail } from "../../../utils/emailValidation";
import { validateClinicName } from "../../../utils/clinicNameValidation";

interface AddTenantFormData {
  clinicName: string;
  ownerFirstName: string;
  ownerLastName: string;
  email: string;
  phoneNumber: string;
}

interface FormErrors {
  clinicName?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  email?: string;
  phoneNumber?: string;
}

interface AddTenantModalContentProps {
  onSuccess: (tenant: Tenant) => void;
  onClose: () => void;
}

const AddTenantModalContent: React.FC<AddTenantModalContentProps> = ({
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AddTenantFormData>({
    clinicName: "",
    ownerFirstName: "",
    ownerLastName: "",
    email: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);

  const getClinicNameError = useCallback(
    (clinicName: string): string | null => {
      const issue = validateClinicName(clinicName);
      if (issue === "required") {
        return t(
          "tenantManagement.addTenant.errors.clinicNameRequired",
          "Clinic name is required",
        );
      }
      if (issue === "tooShort") {
        return t(
          "tenantManagement.addTenant.errors.clinicNameTooShort",
          "Clinic name must be at least 2 characters",
        );
      }
      if (issue === "tooLong") {
        return t(
          "tenantManagement.addTenant.errors.clinicNameTooLong",
          "Clinic name can not be longer than 50 characters",
        );
      }
      if (issue === "onlyNumbers") {
        return t(
          "tenantManagement.addTenant.errors.clinicNameOnlyNumbers",
          "Clinic name can not contain only numbers",
        );
      }
      if (issue === "invalidChars") {
        return t(
          "tenantManagement.addTenant.errors.clinicNameInvalidChars",
          "Clinic name contains invalid characters",
        );
      }
      return null;
    },
    [t],
  );

  const validatePersonFirstName = useCallback(
    (firstName: string): string | null => {
      const issue = validateName(firstName);
      if (issue === "required") {
        return t(
          "tenantManagement.addTenant.errors.firstNameRequired",
          "First name is required",
        );
      }
      if (issue === "tooLong") {
        return t(
          "tenantManagement.addTenant.errors.firstNameTooLong",
          "First name can not be longer than 50 characters.",
        );
      }
      if (issue === "invalid") {
        return t(
          "tenantManagement.addTenant.errors.firstNameInvalid",
          "Please enter a valid name.",
        );
      }
      return null;
    },
    [t],
  );

  const validatePersonLastName = useCallback(
    (lastName: string): string | null => {
      const issue = validateName(lastName);
      if (issue === "required") {
        return t(
          "tenantManagement.addTenant.errors.lastNameRequired",
          "Last name is required",
        );
      }
      if (issue === "tooLong") {
        return t(
          "tenantManagement.addTenant.errors.lastNameTooLong",
          "Last name can not be longer than 50 characters.",
        );
      }
      if (issue === "invalid") {
        return t(
          "tenantManagement.addTenant.errors.lastNameInvalid",
          "Please enter a valid name, using only letters.",
        );
      }
      return null;
    },
    [t],
  );

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const clinicNameError = getClinicNameError(formData.clinicName);
    if (clinicNameError) newErrors.clinicName = clinicNameError;

    const firstNameError = validatePersonFirstName(formData.ownerFirstName);
    if (firstNameError) newErrors.ownerFirstName = firstNameError;

    const lastNameError = validatePersonLastName(formData.ownerLastName);
    if (lastNameError) newErrors.ownerLastName = lastNameError;

    if (!formData.email.trim()) {
      newErrors.email = t(
        "tenantManagement.addTenant.errors.contactEmailRequired",
        "Contact email is required",
      );
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t(
        "tenantManagement.addTenant.errors.contactEmailInvalid",
        "Enter a valid email address",
      );
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = t(
        "tenantManagement.addTenant.errors.phoneRequired",
        "Phone number is required",
      );
    } else if (!validateUSPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = t(
        "tenantManagement.addTenant.errors.phoneInvalid",
        "Enter a valid phone number",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    formData,
    t,
    getClinicNameError,
    validatePersonFirstName,
    validatePersonLastName,
  ]);

  const handleInputChange =
    (field: keyof AddTenantFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
      if (apiError) setApiError(null);
    };

  const handleTrimOnBlur = (field: keyof AddTenantFormData) => () => {
    if (
      field !== "clinicName" &&
      field !== "ownerFirstName" &&
      field !== "ownerLastName" &&
      field !== "email"
    ) {
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: prev[field].trim() }));
  };

  const handleSubmit = useCallback(async () => {
    setApiError(null);
    if (!validate()) return;

    setLoading(true);
    try {
      const params: CreateTenantParams = {
        clinicName: formData.clinicName.trim(),
        ownerFirstName: formData.ownerFirstName.trim(),
        ownerLastName: formData.ownerLastName.trim(),
        email: formData.email.trim(),
        phoneNumber: cleanPhoneNumber(formData.phoneNumber),
      };

      const created = await tenantsService.createTenant(params);
      onSuccess(created);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          data?: {
            detail?: string;
            errors?: Record<string, string[]>;
          };
        };
      };

      const beFieldErrors = axiosError?.response?.data?.errors;

      if (beFieldErrors && typeof beFieldErrors === "object") {
        const fieldMapping: Record<string, keyof FormErrors> = {
          ClinicName: "clinicName",
          OwnerFirstName: "ownerFirstName",
          OwnerLastName: "ownerLastName",
          Email: "email",
          PhoneNumber: "phoneNumber",
        };

        const mapped: FormErrors = {};
        Object.entries(beFieldErrors).forEach(([key, messages]) => {
          const formField = fieldMapping[key];
          if (formField && messages.length > 0) {
            mapped[formField] = messages.join(" ");
          }
        });

        if (Object.keys(mapped).length > 0) {
          setErrors((prev) => ({ ...prev, ...mapped }));
        } else {
          setApiError(
            axiosError.response?.data?.detail ||
              t("tenantManagement.addTenant.errors.createFailed"),
          );
        }
      } else {
        setApiError(
          axiosError?.response?.data?.detail ||
            t("tenantManagement.addTenant.errors.createFailed"),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [formData, validate, onSuccess, t]);

  const allErrors: string[] = [
    errors.clinicName,
    errors.ownerFirstName,
    errors.ownerLastName,
    errors.email,
    errors.phoneNumber,
    apiError ?? undefined,
  ].filter((e): e is string => Boolean(e));

  return (
    <ContentContainer>
      <ModalTitle>
        {t("tenantManagement.addTenant.title", "Add New Tenant")}
      </ModalTitle>
      <ModalSubtitle>
        {t(
          "tenantManagement.addTenant.subtitle",
          "Create a new clinic on the platform",
        )}
      </ModalSubtitle>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.clinicName", "Clinic Name")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <StyledInput
          maxLength={50}
          placeholder={t(
            "tenantManagement.addTenant.clinicNamePlaceholder",
            "e.g. Sunrise Dental Group",
          )}
          value={formData.clinicName}
          onChange={handleInputChange("clinicName")}
          onBlur={handleTrimOnBlur("clinicName")}
          hasError={!!errors.clinicName}
        />
      </FieldContainer>

      <SectionDivider />

      <SectionLabel>
        {t(
          "tenantManagement.addTenant.contactInfoSection",
          "Contact Information",
        )}
      </SectionLabel>

      <InfoBanner>
        <InfoOutlinedIcon
          sx={{ fontSize: 18, flexShrink: 0, mt: "1px", color: "inherit" }}
        />
        <InfoBannerText>
          {t(
            "tenantManagement.addTenant.managerNotice",
            "A user account with the Manager role will be automatically created for the main contact person. They will receive an invitation email and must complete their own registration independently before they can access the platform.",
          )}
        </InfoBannerText>
      </InfoBanner>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.ownerFirstName", "First Name")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <StyledInput
          placeholder={t(
            "tenantManagement.addTenant.ownerFirstNamePlaceholder",
            "e.g. Jane",
          )}
          value={formData.ownerFirstName}
          onChange={handleInputChange("ownerFirstName")}
          onBlur={handleTrimOnBlur("ownerFirstName")}
          hasError={!!errors.ownerFirstName}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.ownerLastName", "Last Name")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <StyledInput
          placeholder={t(
            "tenantManagement.addTenant.ownerLastNamePlaceholder",
            "e.g. Smith",
          )}
          value={formData.ownerLastName}
          onChange={handleInputChange("ownerLastName")}
          onBlur={handleTrimOnBlur("ownerLastName")}
          hasError={!!errors.ownerLastName}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>
          {t(
            "tenantManagement.addTenant.contactEmail",
            "Main Contact Person Email",
          )}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <StyledInput
          type="email"
          placeholder={t(
            "tenantManagement.addTenant.contactEmailPlaceholder",
            "e.g. admin@clinic.com",
          )}
          value={formData.email}
          onChange={handleInputChange("email")}
          onBlur={handleTrimOnBlur("email")}
          hasError={!!errors.email}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.phoneNumber", "Phone Number")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <PhoneInputWrapper>
          <PhoneInput
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange("phoneNumber")}
            placeholder="+1 (___) ___-____"
            error={!!errors.phoneNumber}
          />
        </PhoneInputWrapper>
      </FieldContainer>

      {allErrors.length > 0 && (
        <FormErrorContainer errors={allErrors} style={{ marginTop: 8 }} />
      )}

      <ButtonsContainer>
        <CancelButton variant="outlined" onClick={onClose} disabled={loading}>
          {t("common.cancel", "Cancel")}
        </CancelButton>
        <SubmitButton
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("tenantManagement.addTenant.submit", "Create Tenant")
          )}
        </SubmitButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export { AddTenantModalContent };
export default AddTenantModalContent;
